/** `docs/products/stable.md` "Resume": bio/skills length limits. Mirrors
 * the DB check constraints on `stable_resumes` (`packages/database/src/schema.ts`)
 * — kept in sync deliberately so the API rejects an invalid Resume before
 * ever reaching the database. */
export const RESUME_BIO_MAX_LENGTH = 4_000;
export const RESUME_SKILL_MAX_LENGTH = 60;
export const RESUME_SKILLS_MAX_COUNT = 40;
/** Sorts membership stints most-recent-first (current `ACTIVE` stints
 * before any `REMOVED` one, then by join date) — the natural reading order
 * for a work-history list. Pure so it can be unit tested without a
 * database, and reused identically by both the live Resume read and the
 * frozen snapshot built at application time. */
export function buildWorkHistory(memberships) {
    return [...memberships].sort((a, b) => {
        if (a.status !== b.status)
            return a.status === "ACTIVE" ? -1 : 1;
        return b.joinedAt.getTime() - a.joinedAt.getTime();
    });
}
export class ResumeValidationError extends Error {
}
/** Validates the freeform, Horseman-editable half of a Resume. The verified
 * work-history half is never user-supplied, so it has nothing to validate
 * here. */
export function validateResumeFields(input) {
    const bio = input.bio === null ? null : input.bio.trim();
    if (bio !== null && bio.length > RESUME_BIO_MAX_LENGTH) {
        throw new ResumeValidationError(`A Resume bio cannot exceed ${RESUME_BIO_MAX_LENGTH} characters.`);
    }
    if (input.skills.length > RESUME_SKILLS_MAX_COUNT) {
        throw new ResumeValidationError(`A Resume cannot list more than ${RESUME_SKILLS_MAX_COUNT} skills.`);
    }
    const skills = input.skills.map((skill) => skill.trim()).filter(Boolean);
    for (const skill of skills) {
        if (skill.length > RESUME_SKILL_MAX_LENGTH) {
            throw new ResumeValidationError(`A Resume skill cannot exceed ${RESUME_SKILL_MAX_LENGTH} characters.`);
        }
    }
    return { bio: bio === "" ? null : bio, skills };
}
